import React, { useState } from 'react';
import type { NewUserFormData, PlatformUser, UserRole } from '../../types/user';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Label } from '../../../../components/ui/label';
import { departments } from '../../data/mockUsers';
import { companies } from '../../data/mockUsers';
import { Mail, Phone, Building2, Building, Lock, RefreshCw, Plus } from 'lucide-react';
import { ROLE_PERMISSIONS } from '../../constants/permissions';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../components/ui/dialog';

interface UserFormProps {
  user?: PlatformUser;
  onSubmit: (data: NewUserFormData) => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = React.useState({
    firstName: user?.name.split(' ')[0] || '',
    lastName: user?.name.split(' ')[1] || '',
    email: user?.email || '',
    phoneNumber: user?.profile.phoneNumber || '',
    department: user?.profile.department?.id || '',
    company: user?.profile.company?.id || '',
    initialPassword: '',
    role: user?.role || 'user',
    sendWelcomeEmail: true
  });

  const [isAddingRole, setIsAddingRole] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [isAddingDepartment, setIsAddingDepartment] = useState(false);
  const [newDepartment, setNewDepartment] = useState('');
  const [showPermissions, setShowPermissions] = useState(false);

  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhoneNumber = (phone: string) => {
    if (!phone) return ''; // Phone is optional
    const phoneRegex = /^\+?1?\d{10,14}$/;
    if (!phoneRegex.test(phone.replace(/[-\s()]/g, ''))) {
      return 'Please enter a valid phone number (10-14 digits)';
    }
    return '';
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, email }));
    setErrors(prev => ({ ...prev, email: validateEmail(email) }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setFormData(prev => ({ ...prev, phoneNumber: phone }));
    setErrors(prev => ({ ...prev, phoneNumber: validatePhoneNumber(phone) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhoneNumber(formData.phoneNumber);

    setErrors({
      email: emailError,
      phoneNumber: phoneError
    });

    if (emailError || phoneError) {
      return;
    }

    onSubmit({
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      role: formData.role as UserRole,
      status: 'active',
      groups: [],
      profile: {
        phoneNumber: formData.phoneNumber,
        department: departments.find(d => d.id === formData.department),
        company: companies.find(c => c.id === formData.company)
      },
      twoFactorEnabled: false
    });
  };

  const currentRolePermissions = ROLE_PERMISSIONS[formData.role as UserRole] || [];
  const halfLength = Math.ceil(currentRolePermissions.length / 2);
  const leftColumnPermissions = currentRolePermissions.slice(0, halfLength);
  const rightColumnPermissions = currentRolePermissions.slice(halfLength);

  const handleAddRole = () => {
    if (newRole.trim()) {
      setFormData(prev => ({ ...prev, role: newRole.toLowerCase().replace(/\s+/g, '_') as UserRole }));
      setNewRole('');
      setIsAddingRole(false);
    }
  };

  const handleAddCompany = () => {
    if (newCompany.trim()) {
      const newCompanyId = `company_${Date.now()}`;
      const newCompanyData = {
        id: newCompanyId,
        name: newCompany.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      companies.push(newCompanyData);
      setFormData(prev => ({ ...prev, company: newCompanyId }));
      setNewCompany('');
      setIsAddingCompany(false);
    }
  };

  const handleAddDepartment = () => {
    if (newDepartment.trim()) {
      const newDepartmentId = `dept_${Date.now()}`;
      const newDepartmentData = {
        id: newDepartmentId,
        name: newDepartment.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      departments.push(newDepartmentData);
      setFormData(prev => ({ ...prev, department: newDepartmentId }));
      setNewDepartment('');
      setIsAddingDepartment(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Row 1: Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            required
            placeholder="John"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            required
            placeholder="Doe"
            className="mt-1"
          />
        </div>
      </div>

      {/* Row 2: Email and Phone */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              required
              placeholder="john.doe@example.com"
              className={`pl-9 ${errors.email ? 'border-red-500' : ''}`}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email}</p>
            )}
          </div>
        </div>
        <div>
          <Label htmlFor="phoneNumber">Phone Number</Label>
          <div className="relative mt-1">
            <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              placeholder="+1-555-000-0000"
              className={`pl-9 ${errors.phoneNumber ? 'border-red-500' : ''}`}
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Department and Company */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="department">Department</Label>
          <div className="relative mt-1">
            <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
            <Select
              value={formData.department}
              onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
            >
              <SelectTrigger className="pl-9">
                <SelectValue placeholder="Select or enter department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
                <Dialog open={isAddingDepartment} onOpenChange={setIsAddingDepartment}>
                  <DialogTrigger asChild>
                    <Button
                       className="flex items-start text-sm hover:bg-gray-100 w-full text-[#e04530]"
                      variant="ghost"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Department
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Department</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Department Name</Label>
                        <Input
                          value={newDepartment}
                          onChange={(e) => setNewDepartment(e.target.value)}
                          placeholder="Enter department name"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddDepartment}
                          className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                          disabled={!newDepartment.trim()}
                        >
                          Add Department
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="company">Company</Label>
          <div className="relative mt-1">
            <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
            <Select
              value={formData.company}
              onValueChange={(value) => setFormData(prev => ({ ...prev, company: value }))}
            >
              <SelectTrigger className="pl-9">
                <SelectValue placeholder="Select or enter company name" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
                <Dialog open={isAddingCompany} onOpenChange={setIsAddingCompany}>
                  <DialogTrigger asChild>
                    <Button
                      className="flex items-center w-full px-2 py-1.5 text-sm hover:bg-gray-100 text-[#e04530]"
                      variant="ghost"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Company
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Company</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Company Name</Label>
                        <Input
                          value={newCompany}
                          onChange={(e) => setNewCompany(e.target.value)}
                          placeholder="Enter company name"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddCompany}
                          className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                          disabled={!newCompany.trim()}
                        >
                          Add Company
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Row 4: Password and Role */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="initialPassword">Initial Password</Label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="initialPassword"
              type="text"
              value={formData.initialPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, initialPassword: e.target.value }))}
              required
              placeholder="Enter initial password"
              className="pl-9 pr-9"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1 h-8 w-8 p-0"
              onClick={() => {
                const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
                const password = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                setFormData(prev => ({ ...prev, initialPassword: password }));
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label htmlFor="role">Role</Label>
          <Select
            value={formData.role}
            onValueChange={(value) => setFormData(prev => ({ ...prev, role: value as UserRole }))}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
              <Dialog open={isAddingRole} onOpenChange={setIsAddingRole}>
                <DialogTrigger asChild>
                  <Button
                    className="flex items-center w-full px-2 py-1.5 text-sm hover:bg-gray-100 text-[#e04530]"
                    variant="ghost"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Role
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Role</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Role Name</Label>
                      <Input
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        placeholder="Enter role name"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        onClick={handleAddRole}
                        className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                        disabled={!newRole.trim()}
                      >
                        Add Role
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Welcome Email Checkbox */}
      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="sendWelcomeEmail"
            checked={formData.sendWelcomeEmail}
            onCheckedChange={(checked) => 
              setFormData(prev => ({ ...prev, sendWelcomeEmail: checked as boolean }))
            }
          />
          <Label htmlFor="sendWelcomeEmail" className="text-sm font-normal">
            Send welcome email with login credentials
          </Label>
        </div>
        <p className="text-sm text-gray-500 ml-6">
          User will be prompted to change password on first login
        </p>
      </div>

      {/* Role Permissions */}
      <div>
        <div 
          className="flex items-center justify-between cursor-pointer mb-1 hover:bg-gray-50 p-2 rounded-md transition-colors"
          onClick={() => setShowPermissions(!showPermissions)}
        >
          <Label className={`text-sm font-normal ${showPermissions ? 'text-gray-900' : 'text-gray-500'} flex items-center gap-2`}>
            Role Permissions
            <span className="text-xs text-gray-400">(click to {showPermissions ? 'hide' : 'show'})</span>
          </Label>
        </div>
        {showPermissions && (
          <div className="grid grid-cols-2 gap-2 bg-gray-50 p-3 rounded-lg">
            <div className="space-y-1.5">
              {leftColumnPermissions.map(permission => (
                <div key={permission.id} className="flex items-center space-x-2">
                  <span className="h-2 w-2 bg-red-500 rounded-full" />
                  <span className="text-sm">{permission.name}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              {rightColumnPermissions.map(permission => (
                <div key={permission.id} className="flex items-center space-x-2">
                  <span className="h-2 w-2 bg-red-500 rounded-full" />
                  <span className="text-sm">{permission.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-2">
        <Button type="submit" className="bg-red-500 hover:bg-red-600 text-white">
          Add User
        </Button>
      </div>
    </form>
  );
}; 